import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';

import {
	AlertModal,
	DataCard,
	EmptySearchState,
	ErrorState,
	MainFooter,
	PlanTypeChip,
	SentinelLoading,
	StyledGrid,
} from 'presentation/components';

import {Box, Stack, Typography} from '@mui/material';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {AuthorizationsEmptyState} from 'presentation/components/AuthorizationsEmptyState';
import {ModalError} from 'presentation/components/ModalError';
import {QueryParamsEnum} from 'presentation/constants';
import {useBeneficiaryCard} from 'presentation/hooks';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';

import {useAuthorizations} from '../../../hooks';
import {StyledFooterContainer} from '../../../styles';
import {AuthorizationsOdonto} from '../components/AuthorizationsOdonto';
import {AuthorizationsOdontoFilter} from '../components/AuthorizationsOdontoFilter';
import {useAuthorizationsOdonto} from './hooks/useAuthorizationsOdonto';

const {notice, tryAgain, agreementText, close, changeBeneficiary, emptyFilteredDescription} =
	translations['pt-br'].commons;
const {
	emptySearch,
	errorState,
	scheduleErrorModal: {scheduleErrorTitle, scheduleErrorDescription},
} = translations['pt-br'].privateFlow.requestAuthorizations;
const {title} = translations['pt-br'].privateFlow.requestAuthorizations.businessHoursModal;
const {history} = translations['pt-br'].privateFlow.requestAuthorizations.tabs;

export function AuthorizationsOdontoList() {
	const {setSearchParams} = useQueryParams();
	const {isMobile, isTablet} = useMediaQueries();

	const {beneficiaryCardPlanData} = useBeneficiaryCard();

	const {
		handleChangeBeneficiary,
		modalDescriptionState,
		IS_ERROR,
		IS_UNAVAILABLE,
		handleResetOperationState,
	} = useAuthorizations();

	const {
		items,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		totalItems,
		loadMore,
		resetState,
		refresh,
		hasActiveFilter,
		filteredItemsIsEmpty,
		hasMoreItems,
	} = useAuthorizationsOdonto();

	function handleRemoveFilter() {
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.AUTHORIZATION_STATUS);
			return prev;
		});
	}

	const shouldShowFilter =
		(!itemsListIsEmpty && !itemsListHasError) || (hasActiveFilter && !loadingItemsList);

	const shouldShowTitle = shouldShowFilter || itemsListHasError;

	const shouldShowEmptyWithoutFilter =
		itemsListIsEmpty && !itemsListHasError && !hasActiveFilter && !loadingItemsList;

	const shouldShowEmptyWithFilter =
		filteredItemsIsEmpty && !itemsListHasError && hasActiveFilter && !loadingItemsList;

	return (
		<Fragment>
			<Box>
				<StyledGrid.Root sx={{p: isMobile ? 2 : 4}} spacing={3}>
					<StyledGrid.Item sm={12} md={8} lg={8}>
						{shouldShowTitle ? (
							<Stack pb={2}>
								<Typography variant="h6" color="text.primary">
									{history}
								</Typography>
								{shouldShowFilter && !itemsListHasError ? (
									<AuthorizationsOdontoFilter
										reset={resetState}
										totalItems={totalItems}
										isLoadingItems={loadingItemsList}
									/>
								) : null}
							</Stack>
						) : null}

						{itemsListHasError ? (
							<ErrorState
								title={errorState.title}
								description={errorState.description}
								handleRefresh={refresh}
							/>
						) : null}

						{loadingItemsList && filteredItemsIsEmpty ? (
							<SentinelLoading
								minHeight="200px"
								isLoading={true}
								onVisible={() => {
									if (items?.length > 0) {
										loadMore();
									}
								}}
							/>
						) : null}

						{items && !filteredItemsIsEmpty && !itemsListHasError ? (
							<Fragment>
								<AuthorizationsOdonto authorizations={items} />
								{hasMoreItems ? (
									<SentinelLoading
										minHeight="200px"
										onVisible={() => {
											if (items?.length > 0) {
												loadMore();
											}
										}}
										isLoading={false}
									/>
								) : null}
							</Fragment>
						) : null}
						{shouldShowEmptyWithoutFilter ? (
							<EmptySearchState
								title={emptySearch.noAuthorizationsFound}
								description={emptySearch.noAuthorizationsFoundDescription}
							/>
						) : null}
						{shouldShowEmptyWithFilter ? (
							<AuthorizationsEmptyState
								description={emptyFilteredDescription}
								clearFilter={handleRemoveFilter}
							/>
						) : null}
					</StyledGrid.Item>

					<StyledGrid.Item sm={12} md={4} lg={4}>
						{!isTablet ? (
							<Stack gap={3}>
								<DataCard
									title={beneficiaryCardPlanData?.userName}
									subtitle={beneficiaryCardPlanData?.planName}
									titleColor="text.secondary"
									subtitleColor="text.secondary"
									handleAction={handleChangeBeneficiary}
									buttonText={changeBeneficiary}
									footerComponent={<PlanTypeChip />}
								/>
							</Stack>
						) : null}
					</StyledGrid.Item>
					<AlertModal
						onClose={handleResetOperationState}
						open={IS_UNAVAILABLE}
						headerTitle={notice}
						title={title}
						description={modalDescriptionState}
						variant="info"
						buttonLabel={agreementText}
						data-tag-id={AutoSolEnum.AS_VISUALIZOU_MODAL_HORARIO}
					/>
					<ModalError
						onClose={handleResetOperationState}
						headerTitle={notice}
						description={scheduleErrorDescription}
						open={IS_ERROR}
						title={scheduleErrorTitle}
						firstButton={{
							title: tryAgain,
							handleClick: () => {
								handleResetOperationState();
							},
						}}
						secondButton={{
							title: close,
							handleClick: () => handleResetOperationState(),
						}}
					/>
				</StyledGrid.Root>
			</Box>

			<StyledFooterContainer $mb={108}>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
